import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router/index.js'
import App from './App.vue'

// UnoCSS 样式
import 'virtual:uno.css'

// Vant UI 组件库
import Vant from 'vant'
import 'vant/lib/index.css'

// 移动端触摸模拟器 (开发环境)
import '@vant/touch-emulator'

// 全局样式
import './styles/main.scss'
import './styles/mobile.scss'

// 平台检测
import { platformDetector } from '@/utils/platform.js'
import { initWechat } from '@/utils/wechat.js'
import { runWechatDiagnosis } from '@/utils/wechatDiagnosis.js'

// VConsole调试工具 - 根据环境变量动态加载
// 仅通过 VITE_DEBUG_MODE 控制，设置为字符串 'true' 即开启
const enableVConsole = import.meta.env.VITE_DEBUG_MODE === 'true'
if (enableVConsole) {
  const script = document.createElement('script')
  script.src = 'https://unpkg.com/vconsole@latest/dist/vconsole.min.js'
  script.onload = function() {
    if (window.VConsole) {
      const vConsole = new window.VConsole()
      // info('✅ VConsole已启动')
      // info('📱 当前URL:', window.location.href)
      // info('🌐 UserAgent:', navigator.userAgent)
      // info('🔧 是否微信环境:', /MicroMessenger/i.test(navigator.userAgent))
    }
  }
  script.onerror = function() {
    // error('❌ VConsole加载失败')
  }
  document.head.appendChild(script)
}

// 检测微信环境
const isWeChat = platformDetector.isWeChat()

/**
 * 动态加载微信SDK
 * @returns {Promise<void>}
 */
const loadWechatSDK = () => {
  return new Promise((resolve, reject) => {
    // info('🔧 开始加载微信SDK...')
    
    const script = document.createElement('script')
    script.src = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js'
    script.async = false // 确保同步执行
    
    script.onload = () => {
      // info('✅ 微信SDK脚本加载完成')
      // 等待wx对象就绪
      if (window.wx) {
        resolve()
      } else {
        // 如果wx对象还没就绪，等待一小段时间
        setTimeout(() => {
          if (window.wx) {
            resolve()
          } else {
            reject(new Error('微信SDK加载完成但wx对象不存在'))
          }
        }, 100)
      }
    }
    
    script.onerror = (error) => {
      // error('❌ 微信SDK脚本加载失败:', error)
      reject(new Error('微信SDK脚本加载失败'))
    }
    
    document.head.appendChild(script)
  })
}

/**
 * 初始化微信SDK配置
 * @returns {Promise<void>}
 */
const initWechatSDK = async () => {
  if (!isWeChat) {
    // info('ℹ️ 非微信环境，跳过微信SDK初始化')
    return
  }

  try {
    // 先加载微信SDK脚本
    await loadWechatSDK()
    
    // info('📱 当前URL:', window.location.href)
    // info('🔧 开始初始化微信SDK配置...')
    
    // 初始化微信SDK配置
    const success = await initWechat()
    
    if (success) {
      // info('✅ 微信SDK初始化成功')
      // 初始化成功后运行诊断
      setTimeout(() => runWechatDiagnosis(), 1000)
    } else {
      // warn('⚠️ 微信SDK初始化失败，将在需要时重试')
      // 初始化失败也运行诊断，帮助排查问题
      setTimeout(() => runWechatDiagnosis(), 1000)
    }
  } catch (error) {
    // error('❌ 微信SDK加载或初始化异常:', error)
    // 异常时也运行诊断
    setTimeout(() => runWechatDiagnosis(), 1000)
  }
}

/**
 * 初始化应用
 */
const initApp = async () => {
  // 1. 先初始化微信SDK（如果在微信环境）
  await initWechatSDK()
  
  // 2. 创建应用实例
  const app = createApp(App)
  const pinia = createPinia()
  
  app.use(pinia)
  app.use(router)
  app.use(Vant)
  
  // 3. 挂载应用
  app.mount('#app')
  
  // info('✅ 应用已挂载')
}

// 启动应用
initApp()
